import {useEffect, useMemo, useRef, useState} from 'react'
import {Button, Spinner, Form, Collapse} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import Select from 'react-select'
import {
  PERMISSION_CATALOG,
  ROLES,
  PERMISSION_GROUPS,
  USER_PERMISSIONS,
  CREATE_ROLE,
  UPDATE_ROLE,
  DELETE_ROLE,
  SET_ROLE_PERMISSIONS,
  SET_GROUP_PERMISSIONS,
  SET_USER_OVERRIDES,
  PERMISSION_USERS,
} from '../../../../gql/authz'
import {parseId} from '../../../../helpers'

type Perm = {codename: string; label: string; description: string}
type PermModule = {key: string; title: string; permissions: Perm[]}
type SubjectType = 'role' | 'group' | 'user'
type Ovr = 'inherit' | 'allow' | 'deny'
type Verb = 'view' | 'create' | 'edit' | 'delete' | 'other'

// Quick CRUD grouping from the codename prefix (view_/add_/change_/delete_).
const verbOf = (cn: string): Verb =>
  cn.startsWith('add_') ? 'create'
    : cn.startsWith('change_') ? 'edit'
    : cn.startsWith('delete_') ? 'delete'
    : cn.startsWith('view_') ? 'view'
    : 'other'
const VERB_LABEL: Record<string, string> = {view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete'}

// Subject-focused permission editor: pick a role / group / user and edit its
// permissions in collapsible per-module cards. Roles & groups use on/off
// switches; users use Inherit / Allow / Deny on top of what they inherit.
// Codenames are hidden (shown on hover). Used both in the Permissions center and
// on a single user's page (lockedUser).
export const PermissionEditor = ({
  lockedUser,
  initialSubject,
}: {
  lockedUser?: {id: string; name: string}
  initialSubject?: {type: SubjectType; id: string}
}) => {
  const [subjectType, setSubjectType] = useState<SubjectType>(
    lockedUser ? 'user' : initialSubject?.type ?? 'user'
  )
  const [roleId, setRoleId] = useState(initialSubject?.type === 'role' ? initialSubject.id : '')
  const [groupId, setGroupId] = useState(initialSubject?.type === 'group' ? initialSubject.id : '')
  const [userId, setUserId] = useState(lockedUser?.id || '')
  const [userOpt, setUserOpt] = useState<{value: string; label: string} | null>(
    lockedUser ? {value: lockedUser.id, label: lockedUser.name} : null
  )
  const [userSearch, setUserSearch] = useState('')
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const [draftSet, setDraftSet] = useState<Set<string>>(new Set()) // role/group
  const [draftOvr, setDraftOvr] = useState<Record<string, Ovr>>({}) // user

  const {data: catData, loading: catLoading, error: catError} = useQuery(PERMISSION_CATALOG, {fetchPolicy: 'cache-and-network'})
  const {data: rolesData, refetch: refetchRoles} = useQuery(ROLES, {skip: !!lockedUser, fetchPolicy: 'cache-and-network'})
  const {data: groupsData, refetch: refetchGroups} = useQuery(PERMISSION_GROUPS, {skip: !!lockedUser, fetchPolicy: 'cache-and-network'})
  const {data: usersData, loading: usersLoading, error: usersError} = useQuery(PERMISSION_USERS, {
    variables: {first: 50, isActive: true, name: userSearch || null},
    skip: !!lockedUser || subjectType !== 'user',
    fetchPolicy: 'cache-and-network',
  })
  const {data: upData, loading: upLoading, refetch: refetchUser} = useQuery(USER_PERMISSIONS, {
    variables: {userId},
    skip: subjectType !== 'user' || !userId,
    fetchPolicy: 'cache-and-network',
  })

  const [createRole] = useMutation(CREATE_ROLE)
  const [updateRole] = useMutation(UPDATE_ROLE)
  const [deleteRole] = useMutation(DELETE_ROLE)
  const [setRolePermissions] = useMutation(SET_ROLE_PERMISSIONS)
  const [setGroupPermissions] = useMutation(SET_GROUP_PERMISSIONS)
  const [setUserOverrides] = useMutation(SET_USER_OVERRIDES)
  const [saving, setSaving] = useState(false)

  const modules: PermModule[] = catData?.permissionCatalog?.modules ?? []
  const roles = useMemo(() => rolesData?.roles ?? [], [rolesData])
  const groups = useMemo(() => groupsData?.permissionGroups ?? [], [groupsData])
  const userOptions = useMemo(
    () =>
      (usersData?.allUsers?.edges ?? []).map((e: any) => {
        const name = `${e.node.firstName || ''} ${e.node.lastName || ''}`.trim()
        return {
          value: String(parseId(e.node.id)),
          label: name ? `${name} — ${e.node.email}` : e.node.email,
        }
      }),
    [usersData]
  )

  // Default selection.
  useEffect(() => {
    if (subjectType === 'role' && !roleId && roles.length) setRoleId(roles[0].id)
    if (subjectType === 'group' && !groupId && groups.length) setGroupId(groups[0].id)
  }, [subjectType, roles, groups, roleId, groupId])

  const selectedRole = useMemo(() => roles.find((r: any) => r.id === roleId), [roles, roleId])
  const selectedGroup = useMemo(() => groups.find((g: any) => g.id === groupId), [groups, groupId])
  const up = upData?.userPermissions
  const inherited: Set<string> = useMemo(() => new Set(up?.inherited ?? []), [up])

  // Load drafts when the subject changes.
  useEffect(() => {
    if (subjectType === 'role') setDraftSet(new Set(selectedRole?.permissions ?? []))
  }, [subjectType, selectedRole])
  useEffect(() => {
    if (subjectType === 'group') setDraftSet(new Set(selectedGroup?.permissions ?? []))
  }, [subjectType, selectedGroup])
  useEffect(() => {
    if (subjectType === 'user' && up) {
      const o: Record<string, Ovr> = {}
      ;(up.overrides?.allow ?? []).forEach((c: string) => (o[c] = 'allow'))
      ;(up.overrides?.deny ?? []).forEach((c: string) => (o[c] = 'deny'))
      setDraftOvr(o)
    }
  }, [subjectType, up])

  const isUser = subjectType === 'user'

  // Per-permission state helpers — a checkbox reflects the effective grant for
  // every subject type. For users it stores the MINIMAL override needed:
  //   want granted   → inherit if already inherited, else allow
  //   want NOT granted → deny if inherited, else inherit (clear any allow)
  const ovrOf = (cn: string): Ovr => draftOvr[cn] ?? 'inherit'
  const userEffective = (cn: string) => {
    const o = ovrOf(cn)
    return o === 'allow' ? true : o === 'deny' ? false : inherited.has(cn)
  }
  const permOn = (cn: string) => (isUser ? userEffective(cn) : draftSet.has(cn))

  const setCodesRoleGroup = (codes: string[], on: boolean) =>
    setDraftSet((prev) => {
      const n = new Set(prev)
      codes.forEach((c) => (on ? n.add(c) : n.delete(c)))
      return n
    })
  const setCodesUser = (codes: string[], on: boolean) =>
    setDraftOvr((prev) => {
      const n = {...prev}
      codes.forEach((cn) => {
        const isInh = inherited.has(cn)
        const target: Ovr = on ? (isInh ? 'inherit' : 'allow') : isInh ? 'deny' : 'inherit'
        if (target === 'inherit') delete n[cn]
        else n[cn] = target
      })
      return n
    })
  const setPerms = (codes: string[], on: boolean) =>
    isUser ? setCodesUser(codes, on) : setCodesRoleGroup(codes, on)

  // Search filter.
  const q = search.trim().toLowerCase()
  const matches = (p: Perm) =>
    !q || p.label.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || p.codename.includes(q)
  const visibleModules = modules
    .map((m) => ({...m, permissions: m.permissions.filter(matches)}))
    .filter((m) => m.permissions.length > 0)

  const isExpanded = (key: string) => (q ? true : expanded.has(key))
  const toggleExpand = (key: string) =>
    setExpanded((prev) => {
      const n = new Set(prev)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })

  // Counts per module (effective grants for every subject type).
  const moduleCount = (m: PermModule) => {
    const total = m.permissions.length
    const on = m.permissions.filter((p) => permOn(p.codename)).length
    return {on, total}
  }

  // Dirty tracking.
  const dirty = useMemo(() => {
    if (isUser) {
      const orig: Record<string, Ovr> = {}
      ;(up?.overrides?.allow ?? []).forEach((c: string) => (orig[c] = 'allow'))
      ;(up?.overrides?.deny ?? []).forEach((c: string) => (orig[c] = 'deny'))
      const keys = new Set([...Object.keys(orig), ...Object.keys(draftOvr)])
      return Array.from(keys).some((k) => (orig[k] ?? 'inherit') !== (draftOvr[k] ?? 'inherit'))
    }
    const orig = new Set<string>((subjectType === 'role' ? selectedRole?.permissions : selectedGroup?.permissions) ?? [])
    if (orig.size !== draftSet.size) return true
    return Array.from(draftSet).some((c) => !orig.has(c))
  }, [isUser, up, draftOvr, subjectType, selectedRole, selectedGroup, draftSet])

  const hasSubject = isUser ? !!userId : subjectType === 'role' ? !!roleId : !!groupId

  const handleSave = async () => {
    if (!hasSubject || !dirty) return
    setSaving(true)
    try {
      if (subjectType === 'role') {
        await setRolePermissions({variables: {roleId, codenames: Array.from(draftSet)}})
        await refetchRoles()
      } else if (subjectType === 'group') {
        await setGroupPermissions({variables: {groupId, codenames: Array.from(draftSet)}})
        await refetchGroups()
      } else {
        const allow = Object.keys(draftOvr).filter((k) => draftOvr[k] === 'allow')
        const deny = Object.keys(draftOvr).filter((k) => draftOvr[k] === 'deny')
        await setUserOverrides({variables: {userId, allow, deny}})
        await refetchUser()
      }
      toast.success('Permissions saved.')
    } catch (err: any) {
      toast.error(err?.message || 'Could not save permissions')
    } finally {
      setSaving(false)
    }
  }

  // Role management.
  const newRole = async () => {
    const name = window.prompt('New role name:')?.trim()
    if (!name) return
    try {
      const {data} = await createRole({variables: {name}})
      if (data?.createRole?.success) {
        toast.success(`Role "${name}" created.`)
        await refetchRoles()
        setRoleId(data.createRole.role.id)
      }
    } catch (err: any) { toast.error(err?.message || 'Could not create role') }
  }
  const renameRole = async () => {
    if (!selectedRole) return
    const name = window.prompt('Rename role:', selectedRole.name)?.trim()
    if (!name || name === selectedRole.name) return
    try {
      const {data} = await updateRole({variables: {roleId, name}})
      if (data?.updateRole?.success) { toast.success('Role renamed.'); await refetchRoles() }
    } catch (err: any) { toast.error(err?.message || 'Could not rename role') }
  }
  const removeRole = async () => {
    if (!selectedRole) return
    if (selectedRole.member_count > 0) {
      toast.warning(`"${selectedRole.name}" has ${selectedRole.member_count} member(s). Reassign them first.`)
      return
    }
    if (!window.confirm(`Delete role "${selectedRole.name}"?`)) return
    try {
      const {data} = await deleteRole({variables: {roleId}})
      if (data?.deleteRole?.success) { toast.success('Role deleted.'); setRoleId(''); await refetchRoles() }
    } catch (err: any) { toast.error(err?.message || 'Could not delete role') }
  }

  const changeType = (t: SubjectType) => {
    setSubjectType(t)
    setSearch('')
  }

  if (catError) {
    return <div className='text-center py-10'><h4>Permissions unavailable</h4><p className='text-muted'>{catError.message}</p></div>
  }

  const loading = catLoading || (isUser && upLoading && !up)

  return (
    <div>
      {/* Subject selector */}
      {!lockedUser && (
        <div className='d-flex flex-wrap align-items-center gap-3 mb-4'>
          <div className='btn-group btn-group-sm' role='group'>
            {(['user', 'role', 'group'] as SubjectType[]).map((t) => (
              <button key={t} type='button' className={`btn btn-${subjectType === t ? 'primary' : 'light'}`} onClick={() => changeType(t)}>
                {t === 'role' ? 'Roles' : t === 'group' ? 'Groups' : 'Users'}
              </button>
            ))}
          </div>

          {subjectType === 'role' && (
            <div className='d-flex align-items-center gap-2'>
              <Form.Select size='sm' style={{minWidth: 220}} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                {roles.map((r: any) => <option key={r.id} value={r.id}>{r.name} ({r.member_count})</option>)}
              </Form.Select>
              <Button variant='light' size='sm' title='New role' onClick={newRole}><i className='bi bi-plus fs-5' /></Button>
              <Button variant='light' size='sm' title='Rename' onClick={renameRole} disabled={!selectedRole}><i className='bi bi-pencil' /></Button>
              <Button variant='light-danger' size='sm' title='Delete' onClick={removeRole} disabled={!selectedRole}><i className='bi bi-trash' /></Button>
            </div>
          )}
          {subjectType === 'group' && (
            <Form.Select size='sm' style={{minWidth: 240}} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              {groups.map((g: any) => <option key={g.id} value={g.id}>{g.name} ({g.member_count})</option>)}
            </Form.Select>
          )}
          {subjectType === 'user' && (
            <div style={{minWidth: 280}}>
              <Select
                options={userOptions}
                value={userOpt}
                onChange={(v: any) => { setUserOpt(v); setUserId(v?.value || '') }}
                onInputChange={(v: string) => setUserSearch(v)}
                filterOption={() => true}
                placeholder='Type to search a user…'
                classNamePrefix='rs'
                isLoading={usersLoading}
                noOptionsMessage={() =>
                  usersError ? `Error: ${usersError.message}` : usersLoading ? 'Loading…' : 'Type to search a user'
                }
              />
              {usersError && <div className='text-danger fs-8 mt-1'>{usersError.message}</div>}
            </div>
          )}
        </div>
      )}

      {subjectType === 'group' && (
        <div className='text-muted fs-8 mb-3'>Permissions granted to a group are inherited by all its members.</div>
      )}
      {isUser && hasSubject && (
        <div className='text-muted fs-8 mb-3'>
          Overrides are exceptions on top of what the user inherits from their role{up?.role ? ` (${up.role})` : ''} and groups.
        </div>
      )}

      {/* Toolbar: search + save */}
      <div className='d-flex align-items-center justify-content-between gap-3 mb-3 flex-wrap'>
        <Form.Control
          size='sm' type='search' placeholder='Search permission…'
          value={search} onChange={(e) => setSearch(e.target.value)} style={{maxWidth: 280}}
        />
        <Button variant='primary' size='sm' onClick={handleSave} disabled={saving || !dirty || !hasSubject}>
          {saving ? <Spinner animation='border' size='sm' /> : dirty ? 'Save changes' : 'Saved'}
        </Button>
      </div>

      {loading ? (
        <div className='py-10 text-center'><Spinner animation='border' /></div>
      ) : !hasSubject ? (
        <div className='py-10 text-center text-muted'>Pick a {subjectType} to edit its permissions.</div>
      ) : (
        <div className='d-flex flex-column gap-3'>
          {visibleModules.map((mod) => {
            const {on, total} = moduleCount(mod)
            const allOn = on === total

            // Group by verb; the canonical CRUD permission per verb is the
            // shortest codename with that prefix (e.g. change_lead, not
            // change_lead_personal). Canonical perms live on the header quick
            // toggles; everything else (sensitive/extra) is edited individually.
            const byVerb: Record<Verb, string[]> = {view: [], create: [], edit: [], delete: [], other: []}
            mod.permissions.forEach((p) => byVerb[verbOf(p.codename)].push(p.codename))
            const canonical: Partial<Record<Verb, string>> = {}
            ;(['view', 'create', 'edit', 'delete'] as Verb[]).forEach((v) => {
              if (byVerb[v].length) canonical[v] = byVerb[v].slice().sort((a, b) => a.length - b.length)[0]
            })
            const canonicalSet = new Set(Object.values(canonical))
            const extras = mod.permissions.filter((p) => !canonicalSet.has(p.codename))

            // Expanded body shows only the extras (the canonical CRUD perms are
            // on the header) — but while searching, show every match.
            const bodyPerms = search.trim() ? mod.permissions : extras
            const expandable = bodyPerms.length > 0
            const open = expandable && isExpanded(mod.key)
            const allCodes = mod.permissions.map((p) => p.codename)

            return (
              <div key={mod.key} className='border rounded'>
                <div
                  className='d-flex align-items-center justify-content-between px-4 py-3 flex-wrap gap-2'
                  role={expandable ? 'button' : undefined}
                  onClick={expandable ? () => toggleExpand(mod.key) : undefined}
                >
                  <div className='d-flex align-items-center gap-2'>
                    {expandable && <i className={`bi bi-chevron-${open ? 'down' : 'right'} text-muted`} />}
                    <span className='fw-bold'>{mod.title}</span>
                    <span className={`badge ${on === 0 ? 'badge-light' : allOn ? 'badge-light-success' : 'badge-light-primary'}`}>
                      {on}/{total}
                    </span>
                    {extras.length > 0 && (
                      <span className='text-muted fs-9' title='Extra permissions — expand to edit'>
                        +{extras.length} more
                      </span>
                    )}
                  </div>
                  <div className='d-flex align-items-center gap-4' onClick={(e) => e.stopPropagation()}>
                    {(['view', 'create', 'edit', 'delete'] as Verb[]).map((v) =>
                      canonical[v] ? (
                        <TriCheck
                          key={v}
                          label={VERB_LABEL[v]}
                          checked={permOn(canonical[v] as string)}
                          indeterminate={false}
                          onChange={(val) => setPerms([canonical[v] as string], val)}
                        />
                      ) : null
                    )}
                    <TriCheck
                      label='All'
                      checked={allOn}
                      indeterminate={on > 0 && !allOn}
                      onChange={(val) => setPerms(allCodes, val)}
                    />
                  </div>
                </div>

                <Collapse in={open}>
                  <div>
                    <div className='border-top'>
                      {bodyPerms.map((p, i) => (
                        <div
                          key={p.codename}
                          className={`d-flex align-items-center justify-content-between px-4 py-3 ${i % 2 === 1 ? 'bg-light' : ''}`}
                          title={p.codename}
                        >
                          <div className='pe-3'>
                            <div className='fw-semibold'>{p.label}</div>
                            {p.description ? <div className='text-muted fs-8'>{p.description}</div> : null}
                          </div>
                          <div className='d-flex align-items-center gap-3'>
                            {isUser && (
                              <span className={`badge ${permOn(p.codename) ? 'badge-light-success' : 'badge-light'}`}>
                                {permOn(p.codename) ? 'Granted' : 'No'}
                              </span>
                            )}
                            <Form.Check
                              type='switch'
                              checked={permOn(p.codename)}
                              onChange={(e) => setPerms([p.codename], e.target.checked)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Collapse>
              </div>
            )
          })}
          {visibleModules.length === 0 && <div className='text-muted text-center py-6'>No permissions match “{search}”.</div>}
        </div>
      )}
    </div>
  )
}

// Checkbox with an indeterminate (partial) state, used for the per-module
// View/Create/Edit/Delete/All quick toggles.
const TriCheck = ({
  label,
  checked,
  indeterminate,
  onChange,
}: {
  label: string
  checked: boolean
  indeterminate: boolean
  onChange: (v: boolean) => void
}) => {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked
  }, [indeterminate, checked])
  return (
    <label className='d-flex align-items-center gap-1 mb-0' style={{cursor: 'pointer'}}>
      <input
        ref={ref}
        type='checkbox'
        className='form-check-input'
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className='fs-8 text-gray-700'>{label}</span>
    </label>
  )
}
